/**
 * BeforeAfterBlock — hai hàng Trước / Sau trên thẻ ca của makeup.
 *
 * Đủ ảnh cả hai hàng CỦA CHÍNH MÌNH thì nút "Hoàn tất" mới mở. Server chặn lại
 * lần nữa nên đây thuần là chỉ dẫn để nhân viên biết còn thiếu ảnh nào.
 *
 * Mỗi hàng hiện ĐỦ mọi tấm mình đã gửi (CEO chốt 30/09/2026 — trước đây chỉ
 * hiện tấm đầu nên "Chụp thêm" như không có tác dụng), bấm ảnh để xem lớn
 * (khung 8x10, CEO chốt 27/09/2026), và nút ✕ để gỡ tấm gửi nhầm khi còn hạn
 * nộp. Nút chụp tách riêng khỏi ảnh: gộp hai việc vào một nút thì không còn
 * đường xem lại ảnh vừa chụp.
 */
import { useState } from "react";
import { Camera } from "lucide-react";
import { Button } from "@/ui/button";
import { useOrderProofs } from "./api";
import { ProofCaptureSheet } from "./ProofCaptureSheet";
import { ProofPhotoRow } from "./ProofPhotoRow";
import { myMakeupFaces, myPhotosOfKind, type ProofKind } from "./utils";

interface Props {
  orderId: string;
  /** staff.id của người đang đăng nhập (currentStaff.id). */
  myStaffId: string;
}

const SLOTS: { kind: ProofKind; label: string }[] = [
  { kind: "makeup_before", label: "Trước trang điểm" },
  { kind: "makeup_after", label: "Sau trang điểm" },
];

/** Trần số mặt một thợ ghi được trên một đơn (CHECK ở CSDL). */
const MAX_FACE = 20;

export function BeforeAfterBlock({ orderId, myStaffId }: Props) {
  const { data: proofs = [] } = useOrderProofs(orderId);
  const [open, setOpen] = useState<{ kind: ProofKind; face: number } | null>(null);
  // Mặt vừa thêm mà chưa nộp ảnh nào — chỉ sống trên màn tới khi nộp.
  const [extra, setExtra] = useState<number[]>([]);
  const saved = myMakeupFaces(proofs, myStaffId);
  const faces = [...new Set([1, ...saved, ...extra])].sort((a, b) => a - b);
  const multi = faces.length > 1;
  const nextFace = (faces[faces.length - 1] ?? 1) + 1;

  const slots = (face: number) => (
    <div className="space-y-3">
      {SLOTS.map(({ kind, label }) => {
        const mine = myPhotosOfKind(proofs, myStaffId, kind, face);
        return (
          <div
            key={kind}
            className="space-y-1.5 rounded-md border border-dashed border-[var(--color-info)]/50 p-2"
          >
            <ProofPhotoRow
              orderId={orderId}
              label={label}
              photos={mine}
              emptyText={`Chưa có ảnh ${label.toLowerCase()}`}
            />
            <Button type="button" size="sm" variant="outline" onClick={() => setOpen({ kind, face })}>
              <Camera className="size-4" aria-hidden />
              {mine.length === 0 ? `Chụp ${label.toLowerCase()}` : "Chụp thêm"}
            </Button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="mt-2.5 rounded-md border border-[var(--color-info)]/30 bg-[var(--color-info-bg)]/40 p-2.5">
      <div className="mb-1.5 text-xs font-bold text-[var(--color-info-text)]">
        Ảnh trước / sau trang điểm — bắt buộc để hoàn tất ca
      </div>
      {/* Hai hàng xếp dọc ở mọi cỡ màn: mỗi hàng giờ có thể nhiều tấm, đặt
          cạnh nhau thì thẻ ca lồng nhiều lớp chỉ còn ~99px mỗi ảnh ở bề 390.
          Thợ làm nhiều khách (chấm QC bản 3, 2a): mỗi mặt một nhóm, mỗi mặt
          phải đủ cả trước lẫn sau mới hoàn tất được ca. */}
      {multi ? (
        <div className="space-y-3">
          {faces.map((face) => (
            <section
              key={face}
              aria-label={`Mặt ${face}`}
              className="space-y-1.5 rounded-md border border-[var(--color-info)]/30 bg-white/60 p-2"
            >
              <div className="text-xs font-bold">Mặt {face}</div>
              {slots(face)}
            </section>
          ))}
        </div>
      ) : (
        slots(1)
      )}
      {nextFace <= MAX_FACE ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setExtra((cur) => [...cur, nextFace])}
          >
            + Thêm mặt
          </Button>
          <span className="text-xs text-muted-foreground">
            Làm cho nhiều khách? Mỗi khách một mặt, mỗi mặt nộp đủ trước và sau.
          </span>
        </div>
      ) : null}
      {open ? (
        <ProofCaptureSheet
          orderId={orderId}
          kind={open.kind}
          face={open.face}
          open
          onOpenChange={(o) => {
            if (!o) setOpen(null);
          }}
        />
      ) : null}
    </div>
  );
}
